import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Clock, LogIn, LogOut, Calendar } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function MyAttendancePage() {
  const [status, setStatus] = useState(null);      // null | { clocked_in, since }
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(new Date());

  useEffect(() => { load(); const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const h = await axios.get(`${API}/api/employee-portal/attendance/history`);
      setHistory(h.data.records || h.data || []);
      // derive status from latest record
      const latest = (h.data.records || h.data || [])[0];
      setStatus(latest && !latest.clock_out ? { clocked_in: true, since: latest.clock_in } : { clocked_in: false });
    } catch(e){ if(e?.response?.status !== 403) toast.error('Failed to load attendance'); } finally { setLoading(false); }
  };

  const clock = async (which) => {
    setBusy(true);
    try {
      await axios.post(`${API}/api/employee-portal/attendance/${which}`, {});
      toast.success(which === 'clock-in' ? 'Clocked in.' : 'Clocked out — nice work.');
      await load();
    } catch(e){ if(e?.response?.status !== 403) toast.error(e.response?.data?.detail || `Failed to ${which}`); } finally { setBusy(false); }
  };

  const inMonth = history.filter(r => {
    const d = new Date(r.clock_in || r.date);
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  }).length;

  return (
    <div className="min-h-screen p-6 bg-gray-50" data-testid="employee-attendance-page">
      <div className="max-w-5xl mx-auto space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Attendance & Time-off</h1>
          <p className="text-gray-600 mt-1">Clock in / out, and see your log.</p>
        </div>

        <Card data-testid="attendance-card">
          <CardContent className="p-8 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <div className="text-sm uppercase tracking-wider text-gray-500 mb-1">Current status</div>
              <div className="text-3xl font-bold text-gray-900">
                {status?.clocked_in ? <span className="text-emerald-600">On the clock</span> : <span className="text-gray-500">Off the clock</span>}
              </div>
              {status?.clocked_in && status?.since && (
                <div className="text-sm text-gray-500 mt-1">
                  Since {new Date(status.since).toLocaleTimeString()} · {Math.floor((now - new Date(status.since)) / 60000)} min
                </div>
              )}
            </div>
            <div className="flex gap-3">
              <Button
                data-testid="clock-in-btn"
                onClick={() => clock('clock-in')}
                disabled={busy || status?.clocked_in}
                className="bg-emerald-500 hover:bg-emerald-600 disabled:opacity-40"
              >
                <LogIn className="h-4 w-4 mr-2" /> Clock In
              </Button>
              <Button
                data-testid="clock-out-btn"
                onClick={() => clock('clock-out')}
                disabled={busy || !status?.clocked_in}
                variant="outline"
                className="border-gray-300 disabled:opacity-40"
              >
                <LogOut className="h-4 w-4 mr-2" /> Clock Out
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="grid md:grid-cols-3 gap-4">
          <Card><CardContent className="p-4"><div className="text-sm text-gray-500">Days this month</div><div className="text-2xl font-bold">{inMonth}</div></CardContent></Card>
          <Card><CardContent className="p-4"><div className="text-sm text-gray-500">Total records</div><div className="text-2xl font-bold">{history.length}</div></CardContent></Card>
          <Card><CardContent className="p-4"><div className="text-sm text-gray-500">Now</div><div className="text-2xl font-bold">{now.toLocaleTimeString()}</div></CardContent></Card>
        </div>

        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><Calendar className="h-5 w-5 text-gray-500" /> Recent history</CardTitle></CardHeader>
          <CardContent>
            {loading ? <div className="text-gray-500">Loading…</div> :
              history.length === 0 ? <div className="text-gray-500 italic">No records yet — click Clock In to start.</div> :
              <div className="divide-y divide-gray-100">
                {history.slice(0, 15).map((r, i) => (
                  <div key={i} className="py-3 flex items-center justify-between text-sm">
                    <div>
                      <div className="font-medium text-gray-900">{new Date(r.clock_in || r.date).toLocaleDateString()}</div>
                      <div className="text-xs text-gray-500">
                        In: {r.clock_in ? new Date(r.clock_in).toLocaleTimeString() : '—'} ·
                        Out: {r.clock_out ? new Date(r.clock_out).toLocaleTimeString() : <span className="text-emerald-600">still working</span>}
                      </div>
                    </div>
                    <div className="text-gray-600 font-mono text-xs">
                      {r.hours ? `${r.hours}h` : ''}
                    </div>
                  </div>
                ))}
              </div>}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
